<template>
  <section id="ai" class="py-20 lg:py-32 bg-slate-900 relative overflow-hidden text-white" dir="rtl">
    <div class="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900/40 via-slate-900 to-slate-900 pointer-events-none"></div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/80 text-pink-300 font-semibold text-sm mb-6 border border-white/10">
          <Sparkles class="w-4 h-4" />
          <span>הכירו את העובד החדש שלכם</span>
        </div>
        <h2 class="text-3xl md:text-5xl font-black mb-6 tracking-tight">
          תתעסקו בלקוחות, <br class="hidden sm:block"/>
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">ה-AI שלנו מנהל את העסק.</span>
        </h2>
        <p class="text-base sm:text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
          טיימאיט מציגה את הטכנולוגיה הראשונה מסוגה בעולם הביוטי. בינה מלאכותית שלומדת את העסק שלך, עונה ללקוחות ומבצעת פקודות קוליות.
        </p>
      </div>

      <div class="grid lg:grid-cols-2 gap-8 lg:gap-12">

        <!-- AI Card 1: WhatsApp Bot -->
        <div class="bg-slate-800/80 rounded-[2.5rem] p-8 sm:p-10 border border-slate-700/50 shadow-2xl relative overflow-hidden group hover:border-purple-500/50 transition-colors duration-500">
          <div class="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 70%)"></div>
          <div class="w-16 h-16 bg-purple-500/20 text-purple-400 rounded-2xl flex items-center justify-center mb-8 border border-purple-500/30">
            <Bot class="w-8 h-8" />
          </div>
          <h3 class="text-2xl sm:text-3xl font-black mb-4">וואטסאפ עסקי + AI בוט</h3>
          <p class="text-slate-400 text-base sm:text-lg mb-10 leading-relaxed">
            חברו את הוואטסאפ של העסק שלכם. הבוט החכם שלנו יענה ללקוחות 24/7, יציע תורים פנויים, ויקבע אותם ישירות ביומן בלי שתצטרכו להקליד מילה.
          </p>

          <!-- WhatsApp Mockup -->
          <div class="bg-slate-900 rounded-3xl p-4 sm:p-6 border border-slate-700 shadow-inner">
            <div class="flex flex-col gap-4">
              <!-- Client Message -->
              <div class="bg-slate-800 p-3 sm:p-4 rounded-2xl rounded-tr-sm max-w-[85%] self-start relative border border-slate-700 text-sm">
                <p class="text-slate-300 mb-1 font-bold">לקוחה:</p>
                <p class="text-white">היי, יש תור למחר בערב?</p>
                <span class="text-[10px] text-slate-500 absolute bottom-0 left-3">14:02</span>
              </div>
              <!-- AI Message -->
              <div class="bg-purple-600/20 p-3 sm:p-4 rounded-2xl rounded-tl-sm max-w-[85%] self-end relative border border-purple-500/30 text-sm">
                <p class="text-purple-300 mb-1 font-bold flex items-center gap-1"><Bot class="w-3 h-3"/> הבוט של טיימאיט:</p>
                <p class="text-white">היי שירן! 👋 יש לי תור פנוי מחר ב-17:00 או 18:30 למילוי או לק ג'ל. מה לשריין לך?</p>
                <span class="text-[10px] text-slate-400 absolute bottom-0 right-3">14:02</span>
              </div>
            </div>
          </div>
        </div>

        <!-- AI Card 2: Voice Assistant -->
        <div class="bg-slate-800/80 rounded-[2.5rem] p-8 sm:p-10 border border-slate-700/50 shadow-2xl relative overflow-hidden group hover:border-pink-500/50 transition-colors duration-500 lg:translate-y-12">
          <div class="absolute top-0 left-0 w-64 h-64 rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)"></div>
          <div class="w-16 h-16 bg-pink-500/20 text-pink-400 rounded-2xl flex items-center justify-center mb-8 border border-pink-500/30">
            <Mic class="w-8 h-8" />
          </div>
          <h3 class="text-2xl sm:text-3xl font-black mb-4">עוזרת קולית מובנית ביומן</h3>
          <p class="text-slate-400 text-base sm:text-lg mb-10 leading-relaxed">
            הידיים שלכם עסוקות בלקוח? פשוט דברו אל האפליקציה: "תקבעי לנועה תור למילוי בעוד 3 שבועות" והתור ביומן. קסם.
          </p>

          <!-- Voice Mockup -->
          <div class="bg-slate-900 rounded-3xl p-6 border border-slate-700 shadow-inner flex flex-col items-center justify-center h-[200px]">
            <!-- Glowing Mic / Audio Wave -->
            <div class="relative mb-6">
              <div class="absolute inset-0 rounded-full opacity-50 animate-pulse" style="background: radial-gradient(circle, rgba(236,72,153,0.8) 0%, rgba(236,72,153,0) 70%)"></div>
              <div class="w-16 h-16 bg-slate-800 rounded-full border-2 border-pink-500 flex items-center justify-center relative z-10">
                <Activity class="w-8 h-8 text-pink-400" />
              </div>
            </div>

            <div class="h-6 overflow-hidden">
              <p class="text-white font-bold animate-pulse text-center">"קבעי פדיקור ליעל למחר ב-10..."</p>
            </div>
            <div class="mt-3 bg-emerald-500/20 text-emerald-400 text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 flex items-center gap-1.5 opacity-0 animate-fade-in-delayed">
              <CheckCircle2 class="w-3 h-3"/> התור נקבע ונשלח אישור ליעל
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Sparkles, Bot, Mic, Activity, CheckCircle2 } from 'lucide-vue-next';
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-delayed {
  animation: fadeIn 0.5s ease-out 2s forwards;
}
</style>
